import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import chevronDown from './down-chevron.png';
import '../style.scss';

const InputAutoCompleteOptions = ({ open, options, placeholder, selected, onClick, setOpen }) => {
  const { text, value } = selected;
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const [textState, setTextState] = useState(text);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [activeOption, setActiveOption] = useState(0);

  useEffect(() => {
    setTextState(text);
  }, [selected]);

  const chevronStyle = {
    transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
  };

  const autoCompleteData = useCallback(() => {
    const result = isFiltered ? filteredOptions : options;

    return result;
  }, [options, filteredOptions, isFiltered]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeOption]);

  const onKeyDown = (e) => {
    if (e.keyCode === 13) {
      const selectedIndex = autoCompleteData().findIndex((elem) => elem.value == selected.value);
      const index = activeOption >= selectedIndex && selectedIndex !== -1 ? activeOption + 1 : activeOption;
      handleClick(autoCompleteData()[index]);
      setActiveOption(0);
      inputRef.current.blur();
    } else if (e.keyCode === 38) {
      if (activeOption === 0) {
        return;
      }
      setActiveOption((state) => state - 1);
    } else if (e.keyCode === 40) {
      if (activeOption - 1 === autoCompleteData()?.length) {
        return;
      }
      setActiveOption((state) => state + 1);
    }
  };

  const handleChangeTextState = ({ target: { value } }) => {
    setTextState(value);
    setIsFiltered(!!value);
    onClick('');
    inputRef.current.focus();
    const result = options.filter((elem) => elem.text.toLowerCase().includes(value.toLowerCase()));
    setFilteredOptions(!!value ? result : []);
  };

  const handleClick = (option) => {
    setTextState(option.text);
    setFilteredOptions(options);
    onClick(option.returning || option.value);
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
  };

  const handleOpen = () => {
    if (!open) {
      inputRef.current.focus();
    }
    setOpen(!open);
  };

  return (
    <>
      <div className="input-autocomplete">
        <input
          ref={inputRef}
          type="text"
          className="select-active"
          value={textState}
          placeholder={placeholder}
          onChange={handleChangeTextState}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
        />
        {!!textState && (
          <div className="input-autocomplete-close" onClick={() => handleChangeTextState({ target: { value: '' } })}>
            ×
          </div>
        )}
      </div>
      {open && (
        <div className="select-options scrollable">
          {!!autoCompleteData()?.length &&
            autoCompleteData()
              .filter((option) => option.value !== value)
              .map((option, i) => (
                <div
                  ref={i === activeOption ? scrollRef : null}
                  className={`select-list-option ${i === activeOption ? 'active-select-list-option' : ''}`}
                  key={option.value + i}
                  onClick={(e) => handleClick(option)}
                >
                  {option.text}
                </div>
              ))}
          {!autoCompleteData()?.length && <em className="empty-list-option">Ничего не найдено</em>}
        </div>
      )}
      <img className="select-chevron" src={chevronDown} alt="Down" style={chevronStyle} onClick={handleOpen} />
    </>
  );
};

InputAutoCompleteOptions.propTypes = {
  onClick: PropTypes.func,
  setOpen: PropTypes.func,
  selected: PropTypes.any,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  open: PropTypes.bool,
};

InputAutoCompleteOptions.defaultProps = {
  onClick: () => {},
  setOpen: () => {},
  selected: {},
  placeholder: 'Выберите параметр',
  options: [],
  open: false,
};

export { InputAutoCompleteOptions };
